/**
 * WearItemBar - Progress bar component for wear items
 *
 * Displays a horizontal progress bar with:
 * - Percentage remaining with color coding
 * - Icon and label
 * - Status badge
 */
import React from 'react';
import { Circle, AlertTriangle, AlertCircle, XCircle, CheckCircle } from 'lucide-react';
import { useFormat } from '../../../i18n/format.js';

// Wear item configuration
const WEAR_CONFIG = {
  brake_pad_front: { label: 'Plaquettes AV', group: 'brakes' },
  brake_pad_rear: { label: 'Plaquettes AR', group: 'brakes' },
  tire_front_left: { label: 'Pneu AVG', group: 'tires' },
  tire_front_right: { label: 'Pneu AVD', group: 'tires' },
  tire_rear_left: { label: 'Pneu ARG', group: 'tires' },
  tire_rear_right: { label: 'Pneu ARD', group: 'tires' },
  windshield: { label: 'Pare-brise', group: 'hull' },
  body_condition: { label: 'Carrosserie', group: 'hull' },
  headlights: { label: 'Eclairage AV', group: 'hull' },
  taillights: { label: 'Eclairage AR', group: 'hull' },
  mirrors: { label: 'Retroviseurs', group: 'hull' },
};

// Status configuration
const STATUS_CONFIG = {
  ok: {
    bg: 'bg-green-500',
    bgLight: 'bg-green-100',
    text: 'text-green-700',
    icon: CheckCircle,
    label: 'OK',
  },
  low: {
    bg: 'bg-yellow-500',
    bgLight: 'bg-yellow-100',
    text: 'text-yellow-700',
    icon: AlertTriangle,
    label: 'Bas',
  },
  warning: {
    bg: 'bg-orange-500',
    bgLight: 'bg-orange-100',
    text: 'text-orange-700',
    icon: AlertTriangle,
    label: 'Attention',
  },
  critical: {
    bg: 'bg-red-500',
    bgLight: 'bg-red-100',
    text: 'text-red-700',
    icon: AlertCircle,
    label: 'Critique',
  },
  replace: {
    bg: 'bg-purple-500',
    bgLight: 'bg-purple-100',
    text: 'text-purple-700',
    icon: XCircle,
    label: 'Remplacer',
  },
};

export function WearItemBar({
  type,
  value = 0,
  status = 'ok',
  lastUpdated,
  onClick,
  showStatus = true,
  compact = false,
}) {
  const { fmt } = useFormat();
  const config = WEAR_CONFIG[type] || { label: type, group: 'other' };
  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.ok;
  const StatusIcon = statusConfig.icon;
  const progress = Math.min(100, Math.max(0, value));
  const hasData = !!lastUpdated;

  return (
    <div
      className={`${onClick ? 'cursor-pointer hover:bg-gray-50 transition-colors' : ''} ${compact ? 'py-1' : 'py-2'}`}
      onClick={onClick}
      title={hasData ? `Vie restante: ${Math.round(progress)}% — Dernière MAJ: ${fmt.date(lastUpdated)}` : 'Aucune donnée'}
    >
      {/* Header row */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Circle size={compact ? 10 : 12} className="text-gray-400" />
          <span className={`font-medium ${compact ? 'text-xs' : 'text-sm'} text-gray-700`}>
            {config.label}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`font-semibold ${compact ? 'text-xs' : 'text-sm'} ${hasData ? statusConfig.text : 'text-gray-400'}`}>
            {hasData ? `Usure ${Math.round(100 - progress)}%` : '—'}
          </span>
          {showStatus && (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.bgLight} ${statusConfig.text}`}>
              <StatusIcon size={12} />
              {!compact && statusConfig.label}
            </span>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className={`w-full bg-gray-200 rounded-full ${compact ? 'h-1.5' : 'h-2'}`}>
        <div
          className={`${statusConfig.bg} rounded-full transition-all duration-500 ${compact ? 'h-1.5' : 'h-2'}`}
          style={{ width: hasData ? `${100 - progress}%` : '0%' }}
        />
      </div>
    </div>
  );
}

export { WEAR_CONFIG, STATUS_CONFIG };
export default WearItemBar;
